import { useState, useEffect, useCallback, useRef } from 'react';
import type { Point, ViewportSize } from '@/types';
import { clamp } from '@/utils/geometry';

/** Margin from viewport edges when no boundary exists */
const VIEWPORT_MARGIN = 100;

export interface UseCenterPositionResult {
  /** Current center position */
  center: Point;
  /** Whether the center is being dragged */
  isDragging: boolean;
  /** Start dragging the center */
  startDrag: () => void;
  /** Update center position during drag */
  updateDrag: (point: Point, boundaryPoints: Point[] | null) => void;
  /** End dragging */
  endDrag: () => void;
}

/**
 * Hook that manages the clock center position.
 * Supports dragging with constraints based on viewport or boundary.
 */
export const useCenterPosition = (viewport: ViewportSize): UseCenterPositionResult => {
  const [center, setCenter] = useState<Point>(() => getViewportCenter(viewport));
  const [isDragging, setIsDragging] = useState(false);
  const prevViewportRef = useRef(viewport);

  // Recenter only when viewport dimensions actually change (window resize)
  useEffect(() => {
    const prevViewport = prevViewportRef.current;
    if (
      prevViewport.width !== viewport.width ||
      prevViewport.height !== viewport.height
    ) {
      // Viewport resized - recenter
      setCenter(getViewportCenter(viewport));
      prevViewportRef.current = viewport;
    }
  }, [viewport]);

  const startDrag = useCallback(() => {
    setIsDragging(true);
  }, []);

  const updateDrag = useCallback(
    (point: Point, boundaryPoints: Point[] | null) => {
      if (!isDragging) return;

      let constrainedPoint: Point;

      if (boundaryPoints && boundaryPoints.length > 2) {
        // Constrain to inside boundary
        constrainedPoint = constrainToBoundary(point, boundaryPoints);
      } else {
        // Constrain to viewport with margin
        constrainedPoint = constrainToViewport(point, viewport);
      }

      setCenter(constrainedPoint);
    },
    [isDragging, viewport]
  );

  const endDrag = useCallback(() => {
    setIsDragging(false);
  }, []);

  return {
    center,
    isDragging,
    startDrag,
    updateDrag,
    endDrag,
  };
};

/** Calculate viewport center point */
const getViewportCenter = (viewport: ViewportSize): Point => ({
  x: viewport.width / 2,
  y: viewport.height / 2,
});

/** Constrain point to viewport with margin */
const constrainToViewport = (point: Point, viewport: ViewportSize): Point => ({
  x: clamp(point.x, VIEWPORT_MARGIN, viewport.width - VIEWPORT_MARGIN),
  y: clamp(point.y, VIEWPORT_MARGIN, viewport.height - VIEWPORT_MARGIN),
});

/** Margin from boundary edge (same as viewport margin) */
const BOUNDARY_MARGIN = VIEWPORT_MARGIN;

/** Constrain point to inside boundary polygon with margin */
const constrainToBoundary = (point: Point, boundaryPoints: Point[]): Point => {
  if (!isPointInsidePolygon(point, boundaryPoints)) {
    // Outside boundary - snap to nearest point on boundary (with margin inward)
    const nearestOnBoundary = findNearestPointOnPolygon(point, boundaryPoints);
    return pushPointInward(nearestOnBoundary, boundaryPoints, BOUNDARY_MARGIN);
  }

  // Inside boundary - check if we need to enforce margin
  const { distance, nearestPoint } = getDistanceToBoundary(point, boundaryPoints);
  
  if (distance < BOUNDARY_MARGIN) {
    // Too close to boundary - push away from boundary edge
    return pushAwayFromBoundary(point, nearestPoint, BOUNDARY_MARGIN - distance);
  }

  return point;
};

/** Get distance from point to nearest boundary edge */
const getDistanceToBoundary = (
  point: Point,
  polygon: Point[]
): { distance: number; nearestPoint: Point } => {
  let nearestPoint = polygon[0] ?? point;
  let minDistSq = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % polygon.length];

    if (!p1 || !p2) continue;

    const nearest = nearestPointOnSegment(point, p1, p2);
    const distSq = (nearest.x - point.x) ** 2 + (nearest.y - point.y) ** 2;

    if (distSq < minDistSq) {
      minDistSq = distSq;
      nearestPoint = nearest;
    }
  }

  return { distance: Math.sqrt(minDistSq), nearestPoint };
};

/** Push a point inward from the boundary by the given amount */
const pushPointInward = (point: Point, polygon: Point[], amount: number): Point => {
  // Calculate centroid of polygon
  let cx = 0;
  let cy = 0;
  for (const p of polygon) {
    cx += p.x;
    cy += p.y;
  }
  cx /= polygon.length;
  cy /= polygon.length;

  // Direction from point toward centroid
  const dx = cx - point.x;
  const dy = cy - point.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist < 0.001) {
    return point; // Already at centroid
  }

  // Move point toward centroid by 'amount'
  return {
    x: point.x + (dx / dist) * amount,
    y: point.y + (dy / dist) * amount,
  };
};

/** Push a point away from a boundary point by the given amount */
const pushAwayFromBoundary = (point: Point, boundaryPoint: Point, amount: number): Point => {
  // Direction from boundary point to current point (away from boundary)
  const dx = point.x - boundaryPoint.x;
  const dy = point.y - boundaryPoint.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist < 0.001) {
    // Point is on boundary - need to calculate inward direction differently
    // Just return the point, the user will need to drag further
    return point;
  }

  // Move point away from boundary by 'amount'
  return {
    x: point.x + (dx / dist) * amount,
    y: point.y + (dy / dist) * amount,
  };
};

/** Check if point is inside polygon using ray casting */
const isPointInsidePolygon = (point: Point, polygon: Point[]): boolean => {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];

    if (!pi || !pj) continue;

    const xi = pi.x,
      yi = pi.y;
    const xj = pj.x,
      yj = pj.y;

    if (
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }

  return inside;
};

/** Find the nearest point on the polygon boundary to the given point */
const findNearestPointOnPolygon = (point: Point, polygon: Point[]): Point => {
  let nearestPoint = polygon[0] ?? point;
  let minDistSq = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % polygon.length];

    if (!p1 || !p2) continue;

    // Find nearest point on line segment p1-p2
    const nearest = nearestPointOnSegment(point, p1, p2);
    const distSq = (nearest.x - point.x) ** 2 + (nearest.y - point.y) ** 2;

    if (distSq < minDistSq) {
      minDistSq = distSq;
      nearestPoint = nearest;
    }
  }

  return nearestPoint;
};

/** Find the nearest point on a line segment to a given point */
const nearestPointOnSegment = (point: Point, p1: Point, p2: Point): Point => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    return p1; // Segment is a point
  }

  // Project point onto line, clamped to segment
  const t = clamp(((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSq, 0, 1);

  return {
    x: p1.x + t * dx,
    y: p1.y + t * dy,
  };
};
